import {
  createBrowserRouter,
  Navigate,
  redirect,
  RouterProvider,
} from "react-router-dom";
import { isMobile } from "react-device-detect";
import SignInPage from "@/features/auth/sign-in/sign-in-page";
import PublicLayout from "@/features/ui/layouts/public-layout";
import SignUpPage from "@/features/auth/sign-up/sign-up-page";
import { captureReferral } from "@/features/auth/sign-up/sign-up-referral";
import SignUpPendingPage from "@/features/auth/sign-up-pending/sign-up-pending-page";
import PrivateLayout from "@/features/ui/layouts/private-layout";
import PrivateBareLayout from "@/features/ui/layouts/private-bare-layout";
import NaverSolutionOnboardingPage from "@/features/pathpick/features/naver-solution/onboarding-page";
import Cafe24OnboardingPage from "@/features/pathpick/features/cafe24/onboarding-page";
import Cafe24ResultPage from "@/features/pathpick/features/cafe24/result-page";
import LineSheetSetListPage from "@/features/line-sheet-sets/line-sheet-set-list-page";
import OrderConfirmationInvoiceListPage from "@/features/invoices/order-confirmation-invoices/order-confirmation-invoice-list-page";
import ProformaInvoiceListPage from "@/features/invoices/proforma-invoices/proforma-invoice-list-page";
import OrderSheetSetListPage from "@/features/order-status/order-sheet-set-list-page";
import OrderSheetSetPage from "@/features/order-sheet-sets/order-sheet-set-page";
import LineSheetSetPage from "@/features/line-sheet-sets/line-sheet-set-page";
import DashboardPage from "@/features/dashboard/dashboard-page";
import MobileEntryPage from "@/features/mobile-entry/mobile-entry-page";
import BalanceTransactionListPage from "@/features/balance-transaction-status/balance-transaction-list-page";
import BankRemittanceReceiptListPage from "@/features/invoices/bank-remittance-receipts/bank-remittance-receipt-list-page";
import CreditInvoiceListPage from "@/features/invoices/credit-invoices/credit-invoice-list-page";
import DepositInvoiceListPage from "@/features/invoices/deposit-invoices/deposit-invoice-list-page";
import LandingPage from "@/features/landing/landing";
import PathPickLandingPage from "@/features/pathpick/features/landing";
import _404Page from "@/features/ui/_404-page";
import { Identity } from "@/features/ui/hooks/use-identity";
import {
  applyCompanyIdFromUrl,
  getIdentity,
} from "@/features/ui/helpers/identity-helpers";
import AppLayout from "@/features/ui/app-layout";
import i18next from "i18next";
import { ErrorBoundary } from "react-error-boundary";
import { isSupportedLanguage } from "@/features/ui/app-language-selector";
import _500Page from "@/features/ui/_500-page";
import InventoryListPage from "@/features/inventories/inventory-list-page";
import MyAccountPage from "@/features/my-account/my-account-page";
import LineSheetSetDetailPage from "@/features/line-sheet-sets/detail/line-sheet-set-detail-page";
import BoutiqueAllOrderedProductsPage from "@/features/line-sheet-sets/all-ordered-products/boutique-all-ordered-products-page";
import GmailListPage from "@/features/gmails/agency-gmail-list-page";
import AgencyInquiryListPage from "@/features/inquiries/agency-inquiry-list-page";
import UploadOrderConfirmationInvoicePage from "@/features/invoices/order-confirmation-invoices/upload-order-confirmation-invoice-page";
import OrderConfirmationInvoiceDetailPage from "@/features/invoices/order-confirmation-invoices/order-confirmation-invoice-detail-page";
import UploadProformaInvoicePage from "@/features/invoices/proforma-invoices/upload-proforma-invoice-page";
import ProformaInvoiceDetailPage from "@/features/invoices/proforma-invoices/proforma-invoice-detail-page";
import UploadBankRemittanceReceiptPage from "@/features/invoices/bank-remittance-receipts/upload-bank-remittance-receipt-page";
import BankRemittanceReceiptDetailPage from "@/features/invoices/bank-remittance-receipts/bank-remittance-receipt-detail-page";
import UploadDepositInvoiceUploadPage from "@/features/invoices/deposit-invoices/upload-deposit-invoice-upload-page";
import DepositInvoiceDetailPage from "@/features/invoices/deposit-invoices/deposit-invoice-detail-page";
import UploadCreditInvoicePage from "@/features/invoices/credit-invoices/upload-credit-invoice-page";
import CreditInvoiceDetailPage from "@/features/invoices/credit-invoices/credit-invoice-detail-page";
import UploadLineSheetSetPage from "@/features/line-sheet-sets/upload-line-sheet-set-page";
import UploadTransportationPage from "@/features/transportations/upload-transportation-page";
import TransportationListPage from "@/features/transportations/transportation-list-page";
import TransportationDetailPage from "@/features/transportations/transportation-detail-page";
import UploadPaymentPage from "@/features/invoices/payment/upload-payment-page";
import PaymentListPage from "@/features/invoices/payment/payment-list-page";
import PaymentDetailPage from "@/features/invoices/payment/payment-detail-page";
import TriangularTradeListPage from "@/features/invoices/triangular-trades/triangular-trade-list-page";
import UploadTriangularTradePage from "@/features/invoices/triangular-trades/upload-triangular-trade-page";
import TriangularTradeDetailPage from "@/features/invoices/triangular-trades/triangular-trade-detail-page";
import RedirectTo500WithRefreshing from "@/features/ui/redirectTo500WithRefreshing";
import { reportError } from "@/utils/sentry-report";
import { isProductionApi } from "@/shared/utils/environment";
import ForgotPasswordPage from "@/features/auth/forgot-password/forgot-password-page";
import CenteredSpinner from "@/shared/components/centered-spinner";
import * as ChannelService from "@channel.io/channel-web-sdk-loader";
import MyDashBoardPage from "@/features/my-dashboard";
import SkuDashboardPage from "@/features/sku-dashboard";
import MyOrderOrderHistory from "@/features/my-order/features/order-history";
import OrderHistoryFinalCopy from "@/features/my-order/features/order-history/features/final-copy";
import SettlementManagementPage from "@/features/my-order/features/settlement-management";
import PathPickSearchProductsPage from "@/features/pathpick/features/search-products";
import PathPickBasketPage from "@/features/pathpick/features/basket";
import PathPickSyncDownloadHistoryPage from "@/features/pathpick/features/sync-download-history";
import PathPickOrderHistoryPage from "@/features/pathpick/features/order-history";
import PathPickOrderHistoryDetailPage from "@/features/pathpick/features/order-history/features/detail";
import PathPickMembershipPage from "@/features/pathpick/features/membership";
import PathPickStartGuidePage from "@/features/pathpick/features/start-guide";
import PathPickAccessGuard from "@/features/pathpick/components/PathPickAccessGuard";
import PathPickMobileDeviceBreakpoints from "@/features/pathpick/components/PathPickMobileDeviceBreakpoints";
import OrderAccessGuard from "@/features/order-access/OrderAccessGuard";

// 모바일에서 제공하지 않는 화면들. 로그인 복귀 주소가 여기를 가리키면 모바일 진입 화면으로 돌린다.
const DESKTOP_ONLY_PATHNAMES = ["/dashboard", "/my-dashboard", "/sku-dashboard"];

export function isDesktopOnlyPage(to: string) {
  const path = to.split("?")[0].replace(/^\/(ko|it|en)/, "");
  return DESKTOP_ONLY_PATHNAMES.includes(path);
}

export function getDefaultPageByIdentity(
  identity: Identity | null,
  joinCTA: boolean = false
) {
  if (identity === null) {
    if (joinCTA) {
      // return "/auth/sign-up?utm_source=pathrade-web&utm_medium=product&utm_campaign=product-auth-signup-link&utm_content=cta-button-link-top&utm_term=v240724";
      return "/auth/sign-in?utm_source=pathrade-web&utm_medium=product&utm_campaign=product-auth-signin-link&utm_content=cta-button-link-top&utm_term=v240724";
    }
    return "/auth/sign-in?utm_source=pathrade-web&utm_medium=product&utm_campaign=product-auth-signin-link&utm_content=cta-button-link-top&utm_term=v240724";
  } else if (identity.company === null) {
    return "/auth/sign-up-pending";
  } else if (isMobile) {
    // 모바일은 회사 타입과 무관하게 모바일 전용 진입 화면으로 보낸다.
    // 마켓 대시보드(/dashboard)는 모바일 미제공이고, 에이전시 기본값(/line-sheets)은
    // 모바일 GNB에서 돌아올 길이 마땅치 않다.
    return "/mobile-entry";
  } else if (
    identity.company.type === "BUYER" ||
    identity.company.type === "BOUTIQUE"
  ) {
    // return `/u/${identity.company.id}/dashboard`;
    return `/dashboard`;
  } else {
    return `/line-sheets`;
  }
}

export default function App() {
  // ?company_id=... 로 들어오면 그 회사를 이번 세션의 선택으로 삼는다 (권한 없으면 무시).
  applyCompanyIdFromUrl();

  // GA4/GTM 은 _document.tsx 의 정적 스니펫이 로드한다 — 랜딩으로 들어온 익명 방문자를
  // React 마운트 전에 잡아야 하고, 내부 계정 제외는 GA4 데이터 필터(traffic_type)가 맡는다.
  // 예전에 여기 있던 isBuyer/isTestAccount/ga-disable 게이트는 그래서 사라졌다.
  if (isProductionApi()) {
    ChannelService.loadScript();
  }

  const router = createBrowserRouter([
    {
      path: "/:locale?",
      element: (
        <>
          {/*<ErrorBoundary fallback={<Navigate to={"/500"} replace={true} />}>*/}
          <ErrorBoundary
            fallback={<RedirectTo500WithRefreshing />}
            onError={(err) => {
              // 화면에서 Error 가 아닌 값을 던져도(예: throw { message }) 내용이 남도록 감싸서 보낸다.
              reportError(err, { source: "error-boundary" });
            }}
          >
            <AppLayout />
          </ErrorBoundary>
        </>
      ),
      loader: ({ params, request, ...rest }) => {
        const { locale } = params;
        const url = new URL(request.url);

        // 추천인(?ref=)은 랜딩으로 먼저 들어오는 경우도 있어 최상위에서 잡아둔다.
        captureReferral(url.search);

        if (locale) {
          if (!isSupportedLanguage(locale)) {
            throw redirect("/");
          }
          return null;
        } else if (
          i18next.resolvedLanguage &&
          i18next.resolvedLanguage !== "en" &&
          isSupportedLanguage(i18next.resolvedLanguage)
        ) {
          throw redirect(
            `/${i18next.resolvedLanguage}${url.pathname}${url.search}`
          );
        }
        return null;
      },
      children: [
        {
          path: "auth",
          element: <PublicLayout showBackgroundImage={false} />,
          loader: ({ params, request }) => {
            const identity = getIdentity();
            const url = new URL(request.url);
            const isPending = url.pathname === "/auth/sign-up-pending";
            const isAuthenticated = identity !== null;
            const isConfirmed = isAuthenticated && identity.company !== null;

            if (isAuthenticated && isConfirmed) {
              throw redirect(getDefaultPageByIdentity(identity));
            }

            if (isPending && !isAuthenticated) {
              throw redirect("/auth/sign-in");
            }

            return null;
          },
          children: [
            {
              path: "sign-in",
              element: <SignInPage />,
            },
            {
              path: "sign-up",
              element: <SignUpPage step={1} />,
            },
            {
              path: "sign-up/2",
              element: <SignUpPage step={2} />,
            },
            {
              path: "sign-up/3",
              element: <SignUpPage step={3} />,
            },
            {
              path: "sign-up/4",
              element: <SignUpPage step={4} />,
            },
            {
              path: "sign-up-pending",
              element: <SignUpPendingPage />,
            },
            {
              path: "forgot-password",
              element: <ForgotPasswordPage step={1} />,
            },
            {
              path: "forgot-password/2",
              element: <ForgotPasswordPage step={2} />,
            },
          ],
        },
        {
          path: "",
          element: <LandingPage />,
        },
        {
          // 패스픽 안내 페이지 — 로그인 전에도 열려야 하므로 레이아웃(인증 가드) 없이 둔다.
          path: "pathpick-landing",
          element: <PathPickLandingPage />,
        },
        {
          // 카페24 연동 결과 안내 — 동의 거부/위조 접근처럼 미로그인 상태로 튕겨 오는 경우가
          // 있어 인증 가드 없이 둔다(백엔드 콜백이 status 를 달고 보낸다).
          path: "pathpick/cafe24/result",
          element: <Cafe24ResultPage />,
        },
        {
          path: "",
          element: <PrivateBareLayout />,
          children: [
            {
              path: "pathpick/naver-solution/onboarding",
              element: <NaverSolutionOnboardingPage />,
            },
            {
              // 카페24 앱 설치 온보딩 — 설치로 받은 토큰을 로그인한 바이어에 잇는다.
              path: "pathpick/cafe24/onboarding",
              element: <Cafe24OnboardingPage />,
            },
          ],
        },
        {
          path: "",
          element: <PrivateLayout />,
          loader: ({ params, request }) => {
            //여기에서 redirect하면 replace가 안되어서, PrivateLayout에서 redirect하도록 한다.
            return null;
          },
          children: [
            {
              // 모바일 전용 진입 화면. GNB(햄버거 드로어)가 필요해 PrivateLayout 아래 둔다.
              path: "mobile-entry",
              element: <MobileEntryPage />,
            },
            {
              path: "dashboard",
              element: <DashboardPage />,
            },
            {
              path: "my-dashboard",
              element: <MyDashBoardPage />,
            },
            {
              path: "sku-dashboard",
              element: <SkuDashboardPage />,
            },
            // 오더하기/오더 관리 페이지. 오더가 막힌 계정(user.restricted_features: ORDER)은
            // GNB 메뉴만 감추면 링크·URL로 들어올 수 있어 라우트에서 막는다.
            {
              path: "",
              element: <OrderAccessGuard />,
              children: [
                {
                  path: "line-sheets",
                  children: [
                    {
                      path: "",
                      element: <LineSheetSetListPage />,
                    },
                    {
                      path: ":lineSheetSetId",
                      element: <LineSheetSetPage />,
                    },
                    // 단품오더(api-order) 라우트는 뺐다 — 화면은 src/features/api-order 에 남아 있다.
                  ],
                },
                {
                  path: "line-sheet-sets/upload",
                  element: <UploadLineSheetSetPage />,
                },
                {
                  path: "line-sheet-sets/:lineSheetSetId",
                  element: <LineSheetSetDetailPage />,
                },
                // (부티크) 세트에 들어온 모든 주문을 상품별로 합쳐 보는 조회 화면
                {
                  path: "line-sheet-sets/:lineSheetSetId/ordered-products",
                  element: <BoutiqueAllOrderedProductsPage />,
                },
                {
                  path: "line-sheet-sets/:lineSheetSetId/edit",
                  element: <LineSheetSetDetailPage isEditing={true} />,
                },
                {
                  path: "order-sheets",
                  children: [
                    {
                      path: "",
                      element: <OrderSheetSetListPage />,
                    },
                    {
                      path: ":orderSheetSetId",
                      element: <OrderSheetSetPage />,
                    },
                    {
                      path: "settlement",
                      element: <SettlementManagementPage />,
                    },
                  ],
                },
                {
                  path: "order-sheet-sets",
                  children: [
                    {
                      path: "order-history",
                      children: [
                        {
                          index: true,
                          element: <MyOrderOrderHistory />,
                        },
                        {
                          path: "confirm-detail",
                          element: <OrderHistoryFinalCopy />,
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              path: "deposit-status",
              children: [
                {
                  path: "",
                  element: <BalanceTransactionListPage />,
                },
              ],
            },
            {
              path: "order-confirmation-invoices",
              children: [
                {
                  path: "",
                  element: <OrderConfirmationInvoiceListPage />,
                },
                {
                  path: "upload",
                  element: <UploadOrderConfirmationInvoicePage />,
                },
                {
                  path: ":orderConfirmationInvoiceId",
                  element: (
                    <OrderConfirmationInvoiceDetailPage isEditing={false} />
                  ),
                },
                {
                  path: ":orderConfirmationInvoiceId/edit",
                  element: (
                    <OrderConfirmationInvoiceDetailPage isEditing={true} />
                  ),
                },
              ],
            },
            {
              path: "proforma-invoices",
              children: [
                {
                  path: "",
                  element: <ProformaInvoiceListPage />,
                },
                {
                  path: "upload",
                  element: <UploadProformaInvoicePage />,
                },
                {
                  path: ":proformaInvoiceId",
                  element: <ProformaInvoiceDetailPage />,
                },
                {
                  path: ":proformaInvoiceId/edit",
                  element: <ProformaInvoiceDetailPage isEditing={true} />,
                },
              ],
            },
            {
              path: "payments",
              children: [
                {
                  path: "",
                  element: <PaymentListPage />,
                },
                {
                  path: "upload",
                  element: <UploadPaymentPage />,
                },
                {
                  path: ":paymentId",
                  element: <PaymentDetailPage />,
                },
                {
                  path: ":paymentId/edit",
                  element: <PaymentDetailPage isEditing={true} />,
                },
              ],
            },
            {
              path: "bank-remittance-receipts",
              children: [
                {
                  path: "",
                  element: <BankRemittanceReceiptListPage />,
                },
                {
                  path: "upload",
                  element: <UploadBankRemittanceReceiptPage />,
                },
                {
                  path: ":bankRemittanceReceiptId",
                  element: <BankRemittanceReceiptDetailPage />,
                },
                {
                  path: ":bankRemittanceReceiptId/edit",
                  element: <BankRemittanceReceiptDetailPage isEditing={true} />,
                },
              ],
            },
            {
              path: "deposit-invoices",
              children: [
                {
                  path: "",
                  element: <DepositInvoiceListPage />,
                },
                {
                  path: "upload",
                  element: <UploadDepositInvoiceUploadPage />,
                },
                {
                  path: ":depositInvoiceId",
                  element: <DepositInvoiceDetailPage />,
                },
                {
                  path: ":depositInvoiceId/edit",
                  element: <DepositInvoiceDetailPage isEditing={true} />,
                },
              ],
            },
            {
              path: "credit-invoices",
              children: [
                {
                  path: "",
                  element: <CreditInvoiceListPage />,
                },
                {
                  path: "upload",
                  element: <UploadCreditInvoicePage />,
                },
                {
                  path: ":creditInvoiceId",
                  element: <CreditInvoiceDetailPage />,
                },
                {
                  path: ":creditInvoiceId/edit",
                  element: <CreditInvoiceDetailPage isEditing={true} />,
                },
              ],
            },
            {
              path: "triangular-trades",
              children: [
                {
                  path: "",
                  element: <TriangularTradeListPage />,
                },
                {
                  path: "upload",
                  element: <UploadTriangularTradePage />,
                },
                {
                  path: ":triangularTradeId",
                  element: <TriangularTradeDetailPage isEditing={false} />,
                },
              ],
            },
            {
              path: "inventories",
              element: <InventoryListPage />,
            },
            {
              path: "pathpick",
              children: [
                // 멤버십은 미가입이어도 열려야 한다 (결제로 가는 통로) — 다만 바이어가 아니면 막는다.
                {
                  path: "",
                  element: <PathPickAccessGuard requiresMembership={false} />,
                  children: [
                    {
                      path: "membership",
                      element: <PathPickMembershipPage />,
                    },
                  ],
                },
                // 시작 가이드는 권한이 없어도 열린다 (안내 페이지).
                {
                  path: "start-guide",
                  element: <PathPickStartGuidePage />,
                },
                // 나머지 기능 페이지는 GNB와 같은 기준으로 접근을 막는다 — 링크로 직접 들어와도.
                // 모바일 기기에서는 폭과 관계없이 모바일 배치로 그린다(폴드 펼침·태블릿).
                {
                  path: "",
                  element: (
                    <PathPickMobileDeviceBreakpoints>
                      <PathPickAccessGuard />
                    </PathPickMobileDeviceBreakpoints>
                  ),
                  children: [
                    {
                      path: "search-products",
                      element: <PathPickSearchProductsPage />,
                    },
                    {
                      path: "basket",
                      element: <PathPickBasketPage />,
                    },
                    {
                      path: "sync-download-history",
                      element: <PathPickSyncDownloadHistoryPage />,
                    },
                    {
                      path: "order-history",
                      children: [
                        {
                          path: "",
                          element: <PathPickOrderHistoryPage />,
                        },
                        {
                          path: ":id",
                          element: <PathPickOrderHistoryDetailPage />,
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              path: "my-account",
              element: <MyAccountPage />,
            },
            {
              path: "gmails",
              element: <GmailListPage />,
            },
            {
              path: "inquiries",
              element: <AgencyInquiryListPage />,
            },
            {
              path: "transportations",
              children: [
                {
                  path: "upload",
                  element: <UploadTransportationPage />,
                },
                {
                  path: "",
                  element: <TransportationListPage />,
                },
                {
                  path: ":transportationId",
                  element: <TransportationDetailPage />,
                },
                {
                  path: ":transportationId/edit",
                  element: <TransportationDetailPage isEditing={true} />,
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: "*",
      element: <PublicLayout showBackgroundImage={false} />,
      children: [
        {
          path: "404",
          element: <_404Page />,
        },
        {
          path: "500",
          element: <_500Page />,
        },
        {
          path: "*",
          element: <Navigate to={"/404"} replace={true} />,
        },
      ],
    },
  ]);

  return <RouterProvider router={router} />;
}
